// src/views/pages/restaurant-reservations/waitlist/components/TablesGrid.jsx
// ✅ World-class / enterprise TablesGrid (100+ tables ready)
// - Floor tabs + search + status filters
// - Responsive CSS Grid tiles (never tiny/invisible)
// - Premium tile UI + spacing + right padding
// - Selected Guest bar (ambiguity-free seating) + Clear callback
// - Upcoming reservation conflict detection (45m window)
// - Seat action uses selectedGuest + warns on conflicts
// - Reassign opens drawer (no surprise hidden modal)
// - Uses react-feather icons only
//
// Expected props:
// tables: [{id,label,location,capacity,status}]  status: available|occupied|cleaning
// selectedGuest: waitlist entry object (or null)
// upcomingBookings: array of reservations for today
// seatNow(selectedGuest, tableId)
// openDetails(payload)
// safeNotify(type,msg)
// onClearSelectedGuest()

import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, Space, Typography, Divider, Button, Tag, Input, Tooltip as AntTooltip, Modal } from "antd";
import {
  Search as SearchIcon,
  CheckCircle,
  RotateCcw,
  MapPin,
  Users,
  Clock,
  AlertTriangle,
  X,
  Info,
  Grid as  LayoutGrid,
} from "react-feather";
import moment from "moment";
import { tableStatusPill } from "../utils";

const { Title, Text } = Typography;

const CONFLICT_WINDOW_MIN = 45;

const normalizeStatus = (s) => String(s || "").trim().toLowerCase();
const normalizeLoc = (s) => String(s || "").trim();

function getBookingStartMoment(b) {
  if (!b) return null;

  // ReservationModule shape
  if (b.start) return moment(b.start);

  // ISO fallback
  if (b.time && moment(b.time, moment.ISO_8601, true).isValid()) return moment(b.time);

  // DB listing shape
  const datePart = b.reservation_date ? moment(b.reservation_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
  const timePart = b.reservation_time ? String(b.reservation_time).slice(0, 8) : null;
  if (timePart) return moment(`${datePart} ${timePart}`, "YYYY-MM-DD HH:mm:ss");

  return null;
}

function getBookingTableId(b) {
  return b?.tableId || b?.assigned_table_id || b?.assigned_table || null;
}

function getBookingDisplay(b) {
  return {
    name: b?.customerName || b?.guest_name || b?.name || "Guest",
    party: b?.partySize ?? b?.party_size ?? "-",
    phone: b?.phone || b?.contact_number || "",
  };
}

export default function TablesGrid({
  tables = [],
  openDetails,
  safeNotify,
  seatNow,
  selectedGuest,
  upcomingBookings = [],
  onClearSelectedGuest,
}) {
  // ---------------------------
  // Floors / locations
  // ---------------------------
  const floors = useMemo(() => {
    const set = new Set();
    (tables || []).forEach((t) => {
      const loc = normalizeLoc(t?.location);
      if (loc) set.add(loc);
    });
    return ["All", ...Array.from(set)];
  }, [tables]);

  const [activeFloor, setActiveFloor] = useState("All");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|available|occupied|cleaning|reservedSoon

  // ---------------------------
  // Upcoming reservation conflicts (by table)
  // ---------------------------
  const bookingIndex = useMemo(() => {
    const now = moment();
    const index = new Map(); // tableId -> { booking, startAt, minsToStart }

    (upcomingBookings || []).forEach((b) => {
      const tableId = getBookingTableId(b);
      if (!tableId || tableId === "unassigned") return;

      const startAt = getBookingStartMoment(b);
      if (!startAt || !startAt.isValid()) return;

      const minsToStart = startAt.diff(now, "minutes");
      if (minsToStart < 0) return;

      const existing = index.get(String(tableId));
      if (!existing || minsToStart < existing.minsToStart) {
        index.set(String(tableId), { booking: b, startAt, minsToStart });
      }
    });

    return index;
  }, [upcomingBookings]);

  const reservedSoonSet = useMemo(() => {
    const set = new Set();
    bookingIndex.forEach((v, k) => {
      if (v.minsToStart <= CONFLICT_WINDOW_MIN) set.add(String(k));
    });
    return set;
  }, [bookingIndex]);

  // ---------------------------
  // Filtering tables
  // ---------------------------
  const filteredTables = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();

    return (tables || [])
      .filter((t) => {
        if (activeFloor === "All") return true;
        return normalizeLoc(t.location) === normalizeLoc(activeFloor);
      })
      .filter((t) => {
        const st = normalizeStatus(t.status);
        const isReservedSoon = reservedSoonSet.has(String(t.id));
        if (statusFilter === "all") return true;
        if (statusFilter === "reservedSoon") return isReservedSoon && st === "available";
        return st === statusFilter;
      })
      .filter((t) => {
        if (!query) return true;
        const label = String(t.label || "").toLowerCase();
        const loc = String(t.location || "").toLowerCase();
        const cap = String(t.capacity ?? "").toLowerCase();
        return label.includes(query) || loc.includes(query) || cap.includes(query);
      });
  }, [tables, activeFloor, q, statusFilter, reservedSoonSet]);

  const stats = useMemo(() => {
    const all = tables || [];
    const total = all.length;
    const available = all.filter((t) => normalizeStatus(t.status) === "available").length;
    const occupied = all.filter((t) => normalizeStatus(t.status) === "occupied").length;
    const cleaning = all.filter((t) => normalizeStatus(t.status) === "cleaning").length;
    const reservedSoon = all.filter(
      (t) => normalizeStatus(t.status) === "available" && reservedSoonSet.has(String(t.id))
    ).length;
    return { total, available, occupied, cleaning, reservedSoon };
  }, [tables, reservedSoonSet]);

  // ---------------------------
  // Actions
  // ---------------------------
  const onSeatTable = useCallback(
    async (table) => {
      if (!table?.id) return;

      const st = normalizeStatus(table.status);
      if (st !== "available") {
        safeNotify?.("warning", "This table is not available right now.");
        return;
      }

      if (!selectedGuest?.id) {
        safeNotify?.("warning", "Select a guest from the queue first.");
        return;
      }

      const conflict = bookingIndex.get(String(table.id));
      const isConflict = conflict && conflict.minsToStart <= CONFLICT_WINDOW_MIN;

      if (isConflict) {
        const info = getBookingDisplay(conflict.booking);
        Modal.confirm({
          title: "Reservation conflict risk",
          icon: <AlertTriangle size={18} />,
          content: (
            <div>
              <div style={{ fontWeight: 900, color: "#111827" }}>
                Table {table.label} has a reservation in {Math.max(0, conflict.minsToStart)} minutes.
              </div>
              <div style={{ marginTop: 8, color: "#6b7280" }}>
                {info.name} • {info.party} guests
              </div>
              <div style={{ marginTop: 10, color: "#111827" }}>
                Seat anyway only if you’re sure this guest will finish before the reservation time.
              </div>
            </div>
          ),
          okText: "Seat anyway",
          okType: "danger",
          cancelText: "Cancel",
          onOk: async () => seatNow?.(selectedGuest, table.id),
        });
        return;
      }

      await seatNow?.(selectedGuest, table.id);
    },
    [selectedGuest, seatNow, safeNotify, bookingIndex]
  );

  const openTableDetails = useCallback(
    (t) => {
      openDetails?.({
        type: "table",
        id: t.id,
        tableId: t.id,
        name: t.label,
        partySize: selectedGuest?.partySize || 2,
        phone: selectedGuest?.phone || "",
        location: t.location,
      });
    },
    [openDetails, selectedGuest]
  );

  // ---------------------------
  // UI blocks
  // ---------------------------
  const FloorTabs = () => (
    <div className="ns-floorTabs">
      {floors.slice(0, 14).map((f) => {
        const active = f === activeFloor;
        return (
          <Button
            key={f}
            type={active ? "primary" : "default"}
            size="small"
            className="ns-pillBtn"
            onClick={() => setActiveFloor(f)}
          >
            {f}
          </Button>
        );
      })}
    </div>
  );

  const StatusPills = () => (
    <div className="ns-statusPills">
      <Button
        size="small"
        type={statusFilter === "all" ? "primary" : "default"}
        className="ns-pillBtn"
        onClick={() => setStatusFilter("all")}
      >
        All <Tag className="ns-countTag">{stats.total}</Tag>
      </Button>

      <Button
        size="small"
        type={statusFilter === "available" ? "primary" : "default"}
        className="ns-pillBtn"
        onClick={() => setStatusFilter("available")}
      >
        Available <Tag className="ns-countTag">{stats.available}</Tag>
      </Button>

      <Button
        size="small"
        type={statusFilter === "occupied" ? "primary" : "default"}
        className="ns-pillBtn"
        onClick={() => setStatusFilter("occupied")}
      >
        Occupied <Tag className="ns-countTag">{stats.occupied}</Tag>
      </Button>

      <Button
        size="small"
        type={statusFilter === "cleaning" ? "primary" : "default"}
        className="ns-pillBtn"
        onClick={() => setStatusFilter("cleaning")}
      >
        Cleaning <Tag className="ns-countTag">{stats.cleaning}</Tag>
      </Button>

      <Button
        size="small"
        type={statusFilter === "reservedSoon" ? "primary" : "default"}
        className="ns-pillBtn"
        onClick={() => setStatusFilter("reservedSoon")}
      >
        Reserved Soon <Tag className="ns-countTag">{stats.reservedSoon}</Tag>
      </Button>
    </div>
  );

  const SelectedGuestBar = () => {
    if (!selectedGuest?.id) {
      return (
        <div className="ns-selectedBar ns-selectedBar--empty">
          <Space wrap size={8}>
            <Tag className="ns-chip">Seating</Tag>
            <Text type="secondary">Select a guest from the queue to enable one-click seating.</Text>
          </Space>

          <AntTooltip title="This prevents seating the wrong guest by mistake.">
            <Button icon={<Info size={16} />} className="ns-ghostBtn">
              Help
            </Button>
          </AntTooltip>
        </div>
      );
    }

    return (
      <div className="ns-selectedBar">
        <Space wrap size={8}>
          <Tag className="ns-chip">Selected Guest</Tag>
          {selectedGuest.fromBooking ? <Tag color="green">BOOKED</Tag> : <Tag>WALK-IN</Tag>}
          {selectedGuest.isVIP ? <Tag color="gold">VIP</Tag> : null}
          {selectedGuest.paidSkip ? <Tag color="purple">PAID SKIP</Tag> : null}

          <Text strong style={{ fontSize: 14, color: "#111827" }}>
            {selectedGuest.name || `Guest #${selectedGuest.id}`}
          </Text>

          <Tag color="blue" className="ns-chip">
            {selectedGuest.partySize || "-"} guests
          </Tag>

          {selectedGuest.phone ? <Text type="secondary">{selectedGuest.phone}</Text> : null}
        </Space>

        <Button
          icon={<X size={16} />}
          onClick={() => {
            if (typeof onClearSelectedGuest === "function") onClearSelectedGuest();
            else safeNotify?.("info", "Wire onClearSelectedGuest in parent to clear selection.");
          }}
          className="ns-ghostBtn"
        >
          Clear
        </Button>
      </div>
    );
  };

  const getTileMeta = (t) => {
    const st = normalizeStatus(t.status);
    const conflict = bookingIndex.get(String(t.id));
    const reservedSoon = st === "available" && conflict && conflict.minsToStart <= CONFLICT_WINDOW_MIN;
    const bookingInfo = conflict ? getBookingDisplay(conflict.booking) : null;

    const statusKey = reservedSoon ? "reservedSoon" : st;

    return { st, conflict, reservedSoon, bookingInfo, statusKey };
  };

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Card className="ns-tableCard" bodyStyle={{ padding: 12 }}>
      {/* Header */}
      <div className="ns-headerRow">
        <div className="ns-headerLeft">
          <div className="ns-titleLine">
            <LayoutGrid size={18} />
            <Title level={5} style={{ margin: 0 }}>
              Tables (Live)
            </Title>
          </div>
          <div className="ns-subtitle">Filter by floor and status, then seat the selected guest with one click.</div>
        </div>

        <div className="ns-headerRight">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            allowClear
            placeholder="Search table / floor / capacity…"
            prefix={<SearchIcon size={16} />}
            className="ns-search"
          />
        </div>
      </div>

      <Divider style={{ margin: "10px 0" }} />

      {/* Controls */}
      <div className="ns-controls">
        <FloorTabs />
        <StatusPills />
        <SelectedGuestBar />
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* Grid */}
      {filteredTables.length === 0 ? (
        <div className="ns-empty">
          <Text type="secondary">No tables match your filters.</Text>
        </div>
      ) : (
        <div className="ns-tablesGrid">
          {filteredTables.map((t) => {
            const { st, conflict, reservedSoon, bookingInfo, statusKey } = getTileMeta(t);

            return (
              <div key={t.id} className="ns-tileWrap">
                <Card
                  hoverable
                  className={`ns-tableTile ns-tableTile--${statusKey}`}
                  bodyStyle={{ padding: 12, height: "100%", display: "flex", flexDirection: "column" }}
                  onClick={() => openTableDetails(t)}
                >
                  {/* Accent */}
                  <div className={`ns-accent ns-accent--${statusKey}`} />

                  {/* Top */}
                  <div className="ns-tileTop">
                    <div className="ns-tileLeft">
                      <div className="ns-tableName" title={t.label}>
                        {t.label}
                      </div>

                      <div className="ns-metaRow">
                        <span className="ns-meta">
                          <Users size={12} /> {t.capacity ?? "-"}
                        </span>
                        <span className="ns-meta" title={t.location || "-"}>
                          <MapPin size={12} /> {t.location || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="ns-tileRight">
                      {tableStatusPill(st)}
                      {reservedSoon ? (
                        <Tag color="blue" className="ns-chip ns-chipBlue">
                          <Clock size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                          {Math.max(0, conflict?.minsToStart || 0)}m
                        </Tag>
                      ) : null}
                    </div>
                  </div>

                  {/* Middle */}
                  <div className="ns-tileMid">
                    {reservedSoon && bookingInfo ? (
                      <div className="ns-reservedSoon">
                        <div className="ns-reservedTitle">
                          <AlertTriangle size={14} />
                          Reserved soon
                        </div>
                        <div className="ns-reservedDesc">
                          {bookingInfo.name} • {bookingInfo.party}p
                        </div>
                      </div>
                    ) : (
                      <div className="ns-spacer" />
                    )}
                  </div>

                  {/* Actions */}
                  <div className="ns-actions">
                    <AntTooltip title={st === "occupied" ? "Open table details (reassign inside)" : "Only for occupied tables"}>
                      <Button
                        size="small"
                        icon={<RotateCcw size={14} />}
                        disabled={st !== "occupied"}
                        onClick={(e) => {
                          e.stopPropagation();
                          openTableDetails(t); // ✅ open drawer; reassign from drawer
                        }}
                        className="ns-actionBtn"
                      >
                        Reassign
                      </Button>
                    </AntTooltip>

                    <AntTooltip
                      title={
                        st !== "available"
                          ? "Table not available"
                          : !selectedGuest?.id
                          ? "Select a guest from queue first"
                          : reservedSoon
                          ? "Reserved soon — will confirm"
                          : "Seat selected guest"
                      }
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircle size={14} />}
                        disabled={st !== "available"}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSeatTable(t);
                        }}
                        className="ns-actionBtn"
                      >
                        Seat
                      </Button>
                    </AntTooltip>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Styles (scoped) */}
      <style>{`
        .ns-tableCard{
          border-radius: 16px;
          border: 1px solid #eef2f7;
          box-shadow: 0 10px 30px rgba(17,24,39,.04);
          margin-bottom: 12px;
        }

        .ns-headerRow{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:12px;
        }
        .ns-headerLeft{ min-width:0; }
        .ns-titleLine{
          display:flex; align-items:center; gap:10px;
          color:#111827;
        }
        .ns-subtitle{
          margin-top:6px;
          color:#6b7280;
          font-size:12px;
        }
        .ns-headerRight{ display:flex; justify-content:flex-end; }
        .ns-search{
          width: 320px;
          height: 36px;
          border-radius: 12px;
        }

        .ns-controls{
          display:grid;
          gap:10px;
        }

        .ns-floorTabs{
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }

        .ns-statusPills{
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }

        .ns-pillBtn{
          border-radius: 999px !important;
          height: 32px !important;
          padding: 0 12px !important;
          font-weight: 700;
        }

        .ns-countTag{
          margin-left: 8px !important;
          border-radius: 999px !important;
          font-weight: 800;
        }

        .ns-chip{
          border-radius:999px !important;
          font-weight: 900 !important;
          letter-spacing:.2px;
        }
        .ns-chipBlue{
          margin-inline-end:0 !important;
        }

        .ns-selectedBar{
          background:#fff;
          border:1px solid #eef2f7;
          border-radius:14px;
          padding:10px 12px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          box-shadow: 0 10px 24px rgba(17,24,39,.04);
        }
        .ns-selectedBar--empty{
          background:#fbfcfe;
          border:1px dashed #e5e7eb;
          box-shadow:none;
        }
        .ns-ghostBtn{
          border-radius:12px !important;
          height: 34px !important;
        }

        .ns-empty{
          text-align:center;
          padding:26px 0;
          background:#fbfcfe;
          border:1px dashed #e5e7eb;
          border-radius:14px;
        }

        /* ✅ Premium responsive grid (prevents tiny tiles)
           - Uses min width so cards never collapse
           - Adds right padding so it never "touches"
        */
        .ns-tablesGrid{
          padding-right: 10px;
          padding-bottom: 6px;
          display:grid;
          gap: 14px;
          align-content:start;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }

        /* Prefer 4 tiles on large screens */
        @media (min-width: 1200px){
          .ns-tablesGrid{
            grid-template-columns: repeat(4, minmax(240px, 1fr));
          }
        }
        /* 3 tiles on medium */
        @media (min-width: 900px) and (max-width: 1199px){
          .ns-tablesGrid{
            grid-template-columns: repeat(3, minmax(240px, 1fr));
          }
        }
        /* 2 tiles on small */
        @media (min-width: 600px) and (max-width: 899px){
          .ns-tablesGrid{
            grid-template-columns: repeat(2, minmax(240px, 1fr));
          }
        }
        /* 1 tile on mobile */
        @media (max-width: 599px){
          .ns-search{ width: 100%; }
          .ns-headerRow{ flex-direction:column; align-items:stretch; }
          .ns-headerRight{ justify-content:stretch; }
          .ns-tablesGrid{ grid-template-columns: 1fr; }
        }

        .ns-tileWrap{ height: 100%; }

        .ns-tableTile.ant-card{
          border-radius: 16px !important;
          border: 1px solid #eef2f7 !important;
          background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
          box-shadow: 0 10px 24px rgba(17,24,39,.05);
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          overflow:hidden;
          min-height: 164px; /* consistent feel */
        }
        .ns-tableTile.ant-card:hover{
          transform: translateY(-2px);
          box-shadow: 0 18px 44px rgba(17,24,39,.10);
          border-color: #e6ecf5 !important;
        }

        .ns-accent{
          height: 3px;
          width: 100%;
          opacity: .9;
          margin: -12px -12px 10px -12px; /* stretch to edges */
        }
        .ns-accent--available{ background:#52c41a; }
        .ns-accent--occupied{ background:#ff4d4f; }
        .ns-accent--cleaning{ background:#faad14; }
        .ns-accent--reservedSoon{ background:#1677ff; }

        .ns-tileTop{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:10px;
        }
        .ns-tileLeft{ min-width:0; }
        .ns-tableName{
          font-weight: 900;
          font-size: 14px;
          color:#111827;
          line-height: 1.1;
          white-space: nowrap;
          overflow:hidden;
          text-overflow: ellipsis;
        }
        .ns-metaRow{
          margin-top: 6px;
          color:#6b7280;
          font-size: 12px;
          display:flex;
          gap:10px;
          flex-wrap:wrap;
        }
        .ns-meta{
          display:inline-flex;
          align-items:center;
          gap:6px;
          white-space:nowrap;
        }

        .ns-tileRight{
          display:flex;
          flex-direction:column;
          align-items:flex-end;
          gap:6px;
        }

        .ns-tileMid{
          margin-top: 10px;
          flex: 1;
          min-height: 52px;
        }
        .ns-reservedSoon{
          background:#f0f7ff;
          border:1px solid #d6e4ff;
          border-radius: 12px;
          padding: 8px 10px;
          color:#102a56;
          font-size: 12px;
          line-height: 1.25;
        }
        .ns-reservedTitle{
          font-weight: 900;
          display:flex;
          gap:8px;
          align-items:center;
        }
        .ns-reservedDesc{
          margin-top: 4px;
          opacity: .9;
        }
        .ns-spacer{ height: 44px; }

        .ns-actions{
          display:flex;
          gap: 8px;
          margin-top: 10px;
        }
        .ns-actionBtn{
          flex: 1;
          border-radius: 12px !important;
          height: 32px !important;
        }
      `}</style>
    </Card>
  );
}

TablesGrid.propTypes = {
  tables: PropTypes.array.isRequired,
  openDetails: PropTypes.func.isRequired,
  safeNotify: PropTypes.func.isRequired,
  seatNow: PropTypes.func,
  selectedGuest: PropTypes.object,
  upcomingBookings: PropTypes.array,
  onClearSelectedGuest: PropTypes.func,
};
